import type { WebAppEnvironment } from '@/types/api';
import {
  createFirebaseStoragePath,
  extractStoragePathFromUrl,
  getFirebaseStorageDownloadUrl,
  getFirebaseStorageUploadUrl,
} from '@/utils/firebase-storage';

import { resolveAuthToken } from '../authTokenResolver';
import type {
  FirebaseStorageUrlResponse,
  GetFirebaseStorageUrlConfig,
} from './types';
/**
 * Get a Firebase Storage upload URL with authentication
 * This is similar to the presigned URL approach used for S3
 */
export async function getFirebaseStorageUrlService({
  path = 'media',
  fileName,
  targetEnv,
}: GetFirebaseStorageUrlConfig): Promise<FirebaseStorageUrlResponse> {
  // Create storage path with unique filename
  const { storagePath } = createFirebaseStoragePath(fileName, path);

  // Generate upload URL
  const { uploadUrl, storageBucket } = getFirebaseStorageUploadUrl(storagePath, targetEnv);

  return {
    uploadUrl,
    storagePath,
    storageBucket: storageBucket || '',
  };
}

/**
 * Direct upload to Firebase Storage using XHR and Firebase Storage REST API
 * This approach doesn't use the Firebase client SDK but directly uses the REST API
 * Based on Firebase docs: https://firebase.google.com/docs/storage/web/upload-files
 */
export async function uploadFileToFirebaseStorageService(
  uploadUrl: string,
  file: File,
  onProgress: (progress: number) => void,
  signal: AbortSignal,
  targetEnv: WebAppEnvironment,
): Promise<string> {
  // Get the authentication token for the current user using unified resolver
  const idToken = await resolveAuthToken();
  if (idToken === '') {
    throw new Error('Authentication required: No ID token available');
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.setRequestHeader('Authorization', `Bearer ${idToken}`);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable === true) {
        const percentCompleted = Math.round((event.loaded * 100) / event.total);
        onProgress(percentCompleted);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Parse the response to get the download URL
        const response = JSON.parse(xhr.responseText);
        const downloadToken = response.downloadTokens;

        // Extract storage path from the upload URL
        const storagePath = extractStoragePathFromUrl(uploadUrl);

        // Construct download URL
        const downloadUrl = getFirebaseStorageDownloadUrl(storagePath, downloadToken, targetEnv);

        resolve(downloadUrl);
      } else {
        // Enhanced error reporting for debugging
        let errorMessage = `Upload failed with status ${xhr.status}`;

        // Try to parse the error response or just use the raw response
        try {
          const errorResponse = JSON.parse(xhr.responseText);
          errorMessage = `${errorMessage}: ${JSON.stringify(errorResponse)}`;
        } catch {
          // If not JSON, just use the raw text
          errorMessage = `${errorMessage}: ${xhr.responseText}`;
        }

        // Log more details about the request for debugging
        console.error('Upload failed details:', {
          status: xhr.status,
          responseText: xhr.responseText,
          url: uploadUrl,
          fileType: file.type,
          fileSize: file.size,
          hasToken: idToken !== '',
          tokenLength: idToken.length,
          // Log a partial token (first few characters) for debugging
          // Don't log the full token for security reasons
          tokenStart: idToken ? `${idToken.substring(0, 10)}...` : 'none',
        });

        reject(new Error(errorMessage));
      }
    };

    xhr.onerror = (event) => {
      console.error('Network error during upload:', event);
      reject(new Error('Upload failed due to network error'));
    };

    xhr.ontimeout = () => {
      reject(new Error('Upload timed out'));
    };

    signal.addEventListener('abort', () => {
      xhr.abort();
      reject(new Error('Upload cancelled'));
    });

    try {
      xhr.send(file);
    } catch (error) {
      console.error('Error sending file:', error);
      reject(error);
    }
  });
}
